import { Check, ScanFace, Sparkles } from "lucide-react";
import { CAPTION_STYLES, FRAMINGS, HOOK_STYLES, PACINGS, PLATFORMS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PlatformTile } from "@/components/PlatformTile";

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const SectionHead = ({ label, meta }) => (
  <div className="flex items-center justify-between">
    <p className="section-label">{label}</p>
    {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
  </div>
);

const ChipGroup = ({ label, options, value, onChange, testid, render, meta }) => (
  <div className="space-y-2">
    <SectionHead label={label} meta={meta ?? (value.length ? `${value.length} selected` : "All styles")} />
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const id = typeof o === "string" ? o : o.id;
        const active = value.includes(id);
        return (
          <button key={id} type="button" onClick={() => onChange(toggle(value, id))} className={`chip ${active ? "chip-active" : "chip-idle"}`} data-testid={`${testid}-${id}`} aria-pressed={active}>
            {render ? render(o) : id}
          </button>
        );
      })}
    </div>
  </div>
);

const PlatformGrid = ({ value, onChange }) => (
  <div className="space-y-2">
    <SectionHead label="Share to" meta={value.length === PLATFORMS.length ? "All selected" : `${value.length} selected`} />
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {PLATFORMS.map((p) => {
        const active = value.includes(p.id);
        return (
          <button key={p.id} type="button" onClick={() => onChange(toggle(value, p.id))} className={`platform-card ${active ? "platform-card-active" : "platform-card-idle"}`} data-testid={`platform-${p.id}`} aria-pressed={active}>
            <PlatformTile id={p.id} />
            <span className="flex-1 min-w-0"><span className="block text-sm font-semibold truncate">{p.label}</span><span className="block text-[10px] font-mono text-muted-foreground">{p.framing}</span></span>
            <span className={`h-5 w-5 rounded-full grid place-items-center ${active ? "bg-primary text-primary-foreground" : "border border-border"}`}>{active && <Check size={12} strokeWidth={3} />}</span>
          </button>
        );
      })}
    </div>
  </div>
);

export const PreferencesPanel = ({ prefs, setPrefs, onGenerate, disabled, generating, credits }) => {
  const set = (k) => (v) => setPrefs({ ...prefs, [k]: v });
  return (
    <div className="card-surface p-6 space-y-6" data-testid="preferences-panel">
      <span className="overline-label">Step 2 · Targets & editing preferences</span>
      <PlatformGrid value={prefs.platforms} onChange={set("platforms")} />
      <ChipGroup label="Opening hook style" options={HOOK_STYLES} value={prefs.hook_styles} onChange={set("hook_styles")} testid="hook" />
      <ChipGroup label="Caption style" options={CAPTION_STYLES} value={prefs.caption_styles} onChange={set("caption_styles")} testid="caption" />
      <ChipGroup label="Pacing" options={PACINGS} value={prefs.pacings} onChange={set("pacings")} testid="pacing" render={(p) => ({ cinematic: "Cinematic 1.0×", natural: "Natural 1.1×", fast: "Fast 1.25×" }[p])} />
      <ChipGroup label="Framing override" options={FRAMINGS} value={prefs.framings} onChange={set("framings")} testid="framing" meta={prefs.framings.length ? `${prefs.framings.length} selected` : "From platforms"} />
      <div className="flex items-center justify-between rounded-xl border border-border p-4">
        <div>
          <p className="font-semibold text-sm flex items-center gap-2"><ScanFace size={15} className="text-primary" /> Smart framing</p>
          <p className="text-xs text-muted-foreground mt-0.5">Vertical and square crops follow detected faces so speakers stay centered.</p>
        </div>
        <Switch checked={prefs.smart_framing} onCheckedChange={set("smart_framing")} data-testid="smart-framing-switch" />
      </div>
      <div className="space-y-2">
        <SectionHead label="Variations" meta={`${prefs.count} credits`} />
        <div className="flex gap-2">
          {[1, 10, 25, 50].map((n) => (
            <button key={n} type="button" onClick={() => set("count")(n)} className={`chip font-mono ${prefs.count === n ? "chip-active" : "chip-idle"}`} data-testid={`count-${n}`}>{n}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border">
        <p className="text-xs text-muted-foreground font-mono">Cost: {prefs.count} credits · balance {credits}</p>
        <Button onClick={onGenerate} disabled={disabled || generating || prefs.platforms.length === 0} className="btn-pill h-11 px-6" data-testid="generate-button">
          <Sparkles size={15} className="mr-2" /> {generating ? "Starting batch…" : `Generate ${prefs.count} variation${prefs.count > 1 ? "s" : ""} for ${prefs.platforms.length} platform${prefs.platforms.length === 1 ? "" : "s"}`}
        </Button>
      </div>
    </div>
  );
};
